import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getTopic } from "../api/client";
import {
  useDeleteRecap,
  useCreateResponse,
  useCreateOrGetRecap,
} from "../api/mutations";
import { useNotification } from "../hooks/useNotification";
import { ModuleI, LessonI, FeedbackI } from "../types";
import { CreateFeedbackRequest } from "../api/types";
import Button from "../componentLibrary/Button";
import {
  handleLessonCompletion,
  shouldShowSaveModal,
} from "../utils/lessonCompletion";
import SaveAnswersModal from "./SaveAnswersModal";

interface LessonCompletionButtonProps {
  moduleId: string;
  lessonId: string;
  handleSaveAndContinue: (shouldSaveToToolbox?: boolean) => Promise<void>;
  saveAnswersToLog: boolean | undefined;
  askEveryTime: boolean | undefined;
  recapId?: string;
  lessonTitle?: string;
  hasAnswers: boolean;
  destination: "next-lesson" | "module-menu";
  buttonVariant?: "primary" | "secondary";
  feedback?: FeedbackI | null;
  feedbackMutation?: {
    mutateAsync: (data: CreateFeedbackRequest) => Promise<unknown>;
  };
}

export default function LessonCompletionButton({
  moduleId,
  lessonId,
  handleSaveAndContinue,
  saveAnswersToLog,
  askEveryTime,
  recapId,
  lessonTitle,
  hasAnswers,
  destination,
  buttonVariant = "primary",
  feedback,
  feedbackMutation,
}: LessonCompletionButtonProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [moduleInfo, setModuleInfo] = useState<{
    module?: ModuleI;
    nextLesson?: LessonI;
  }>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { notify } = useNotification();

  const deleteRecapMutation = useDeleteRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: async () => {
      notify("error", "There was an error clearing the recap.");
    },
  });

  const createResponseMutation = useCreateResponse({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["responses"] });
    },
    onError: async () => {
      notify("error", "There was an error marking lesson as complete.");
    },
  });

  const createOrGetRecapMutation = useCreateOrGetRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: async () => {
      notify("error", "There was an error.");
    },
  });

  const getTopicInfo = async () => {
    try {
      const module = await getTopic(moduleId);
      if (destination === "next-lesson") {
        const currentLessonIndex = module.lessons.findIndex(
          (lesson) => lesson._id === lessonId,
        );
        const nextLesson = module.lessons[currentLessonIndex + 1];
        return { module, nextLesson };
      }
      return { module };
    } catch (error) {
      return { module: null, nextLesson: null };
    }
  };

  const handleAction = async () => {
    // Check if user wants to be asked every time
    if (shouldShowSaveModal(askEveryTime, hasAnswers)) {
      // Ensure we have the latest module info before showing the modal
      const latestModuleInfo = await getTopicInfo();
      setModuleInfo(latestModuleInfo);
      setShowSaveModal(true);
      return;
    }

    // Use the preference value (default to false if not set)
    const shouldSaveAnswers = saveAnswersToLog ?? true;

    await handleLessonCompletion(
      {
        saveAnswers: shouldSaveAnswers,
        recapId,
        lessonTitle,
        moduleId,
        lessonId,
        hasAnswers,
        feedback,
      },
      {
        handleSaveAndContinue: () => handleSaveAndContinue(shouldSaveAnswers),
        deleteRecapMutation,
        createResponseMutation,
        feedbackMutation,
        notify,
      },
    );

    // Navigate based on destination
    if (destination === "next-lesson") {
      if (moduleInfo.nextLesson) {
        createOrGetRecapMutation.mutate({
          lesson: moduleInfo.nextLesson._id,
          module: moduleId,
        });
        navigate(
          `/topics/${moduleInfo.module?.slug ?? moduleId}/sections/${moduleInfo.nextLesson.slug ?? moduleInfo.nextLesson._id}?page=0`,
        );
      } else {
        navigate(`/topics/${moduleInfo.module?.slug ?? moduleId}`);
      }
    } else {
      navigate(`/topics/${moduleInfo.module?.slug ?? moduleId}`);
    }
  };

  const handleModalConfirm = async (saveAnswers: boolean) => {
    await handleLessonCompletion(
      {
        saveAnswers,
        recapId,
        lessonTitle,
        moduleId,
        lessonId,
        hasAnswers,
        feedback,
      },
      {
        handleSaveAndContinue: () => handleSaveAndContinue(saveAnswers),
        deleteRecapMutation,
        createResponseMutation,
        feedbackMutation,
        notify,
      },
    );

    // Navigate based on destination
    if (destination === "next-lesson") {
      if (moduleInfo.nextLesson) {
        createOrGetRecapMutation.mutate({
          lesson: moduleInfo.nextLesson._id,
          module: moduleId,
        });
        navigate(
          `/topics/${moduleInfo.module?.slug ?? moduleId}/sections/${moduleInfo.nextLesson.slug ?? moduleInfo.nextLesson._id}?page=0`,
        );
      } else {
        navigate(`/topics/${moduleInfo.module?.slug ?? moduleId}`);
      }
    } else {
      navigate(`/topics/${moduleInfo.module?.slug ?? moduleId}`);
    }
  };

  useEffect(() => {
    getTopicInfo().then(setModuleInfo);
  }, [moduleId, lessonId, destination]);

  const getButtonText = () => {
    if (destination === "next-lesson") {
      return moduleInfo.nextLesson
        ? `Continue to ${moduleInfo.nextLesson.title}`
        : `Back to ${moduleInfo.module?.title || "module"}`;
    } else {
      return `Return to Menu for ${moduleInfo.module?.title || "module"}`;
    }
  };

  return (
    <>
      <Button
        className="w-full h-fit"
        variant={buttonVariant}
        roundedFull
        onClick={handleAction}
        size="md"
      >
        {getButtonText()}
      </Button>

      <SaveAnswersModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleModalConfirm}
      />
    </>
  );
}
